/**
 * FeatureGate — wraps content behind a subscription check.
 * Shows children if subscribed, or a locked card with upgrade CTA.
 */

import { useState } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionModal from './SubscriptionModal';

interface Props {
  feature: string;
  children: React.ReactNode;
  title?: string;
  description?: string;
}

const FEATURE_DETAILS: Record<string, { title: string; desc: string; features: string[] }> = {
  deep_analysis: {
    title: 'Deep Analysis — Pro Feature',
    desc: 'Get an investor-grade analysis combining all your pipeline data, validation results, and market signals into one comprehensive report.',
    features: [
      '✓ Executive Summary (investor-ready)',
      '✓ Full competitive landscape with battle cards',
      '✓ Financial projections (3 scenarios)',
      '✓ Go-to-market strategy with week-by-week tactics',
      '✓ Risk matrix with mitigation strategies',
      '✓ Investor pitch narrative',
      '✓ 90-day roadmap',
    ],
  },
};

export default function FeatureGate({ feature, children, title, description }: Props) {
  const { hasFeature } = useSubscription();
  const [showModal, setShowModal] = useState(false);

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  const details = FEATURE_DETAILS[feature];

  return (
    <>
      <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden' }}>
        {/* Blurred preview of actual content */}
        <div style={{
          filter: 'blur(6px)', opacity: 0.25, pointerEvents: 'none',
          userSelect: 'none', maxHeight: 320, overflow: 'hidden',
        }}>
          {children}
        </div>

        {/* Lock overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(5,5,16,0.7)',
          backdropFilter: 'blur(2px)',
        }}>
          <div style={{
            background: 'rgba(13,13,31,0.95)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 20, padding: '36px 32px', textAlign: 'center',
            maxWidth: 420, width: '90%',
            boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          }}>
            <span style={{ fontSize: 36, marginBottom: 12, display: 'block' }}>🔒</span>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
              {title || details?.title || 'Pro Feature'}
            </h3>
            <p style={{ fontSize: 13, color: '#8888aa', marginBottom: 20, maxWidth: 380, margin: '0 auto 20px' }}>
              {description || details?.desc || 'Upgrade to Pro to unlock this feature.'}
            </p>

            {details?.features && (
              <div style={{ textAlign: 'left', maxWidth: 340, margin: '0 auto 24px' }}>
                {details.features.map((f, i) => (
                  <p key={i} style={{ fontSize: 13, color: '#d1d5db', padding: '4px 0' }}>{f}</p>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowModal(true)}
              style={{
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                border: 'none', borderRadius: 12, color: '#fff',
                fontSize: 15, fontWeight: 700, padding: '12px 28px', cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(249,115,22,0.3)',
              }}
            >
              → Upgrade to Pro — $29/month
            </button>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>Cancel anytime · No contracts</p>
          </div>
        </div>
      </div>

      {showModal && <SubscriptionModal onClose={() => setShowModal(false)} />}
    </>
  );
}
